import React, { Component, ErrorInfo, ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from "lucide-react";
import { logger } from "@/lib/logger";

interface Props {
  children: ReactNode;
  platformName?: string;
  backPath?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/** Error boundary that catches render errors in platform dashboards and shows a recovery UI. */
class PlatformErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const route = typeof window !== "undefined" ? window.location.pathname : "unknown";
    logger.error("[PlatformErrorBoundary]", {
      platform: this.props.platformName ?? "unknown",
      route,
      message: error.message,
      name: error.name,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
    // Loud, structured console marker so future P0s are easy to spot during audits.
    console.error(
      `[P0 route failure] platform=${this.props.platformName ?? "unknown"} route=${route} :: ${error.message}`,
      { error, errorInfo }
    );
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleGoBack = () => {
    const { backPath } = this.props;
    if (backPath) {
      window.location.href = backPath;
    } else {
      window.history.back();
    }
  };

  private handleGoHome = () => {
    window.location.href = "/";
  };

  public render() {
    if (this.state.hasError) {
      const { platformName = "This page" } = this.props;
      
      return (
        <div className="min-h-[50vh] flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="text-xl font-display font-bold mb-2 text-foreground">
              Something went wrong
            </h2>
            <p className="text-muted-foreground mb-6">
              {platformName} encountered an error. Please try again or go back.
            </p>
            {this.state.error && (
              <div className="text-left text-xs text-muted-foreground bg-muted p-3 rounded mb-4 font-mono overflow-auto space-y-1">
                <div><span className="font-semibold">Route:</span> {typeof window !== "undefined" ? window.location.pathname : "—"}</div>
                <div><span className="font-semibold">Platform:</span> {platformName}</div>
                <div><span className="font-semibold">Error:</span> {this.state.error.message}</div>
              </div>
            )}
            <div className="flex gap-3 justify-center flex-wrap">
              <Button variant="outline" onClick={this.handleRetry}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button variant="outline" onClick={this.handleGoBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
              <Button onClick={this.handleGoHome}>
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default PlatformErrorBoundary;
